import { useEffect, useRef } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { Loader2 } from 'lucide-react';

function CrossDomainLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#2a20ad] to-[#655bf1]">
      <div className="flex flex-col items-center gap-4">
        <img src="/logos/logo-fluity-white.svg" alt="Fluity" className="h-10 w-auto object-contain" />
        <div className="h-7 w-7 rounded-full border-[3px] border-[#e0dbff] border-t-white animate-spin" />
        <p className="text-white/70 text-sm">Entrando no Fluity Chat...</p>
      </div>
    </div>
  )
}

interface OnboardingGuardProps {
  children?: React.ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { organizations, loading: orgLoading, loadError } = useOrganizationContext();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const isPostCrossDomainLogin = sessionStorage.getItem('fluity-post-login') === 'cross_domain';

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    if (authLoading || orgLoading) return;

    // Limpar flag após o carregamento estar completo
    sessionStorage.removeItem('fluity-post-login');

    const currentPath = window.location.pathname;

    // Falha de conexão/sessão: não confundir com "sem organização"
    if (organizations.length === 0 && loadError) {
      if (currentPath !== '/pending-access') {
        navigateRef.current('/pending-access', { replace: true });
      }
      return;
    }

    // Sem org → bloquear acesso e exibir tela de aguardo
    if (organizations.length === 0) {
      navigateRef.current('/pending-access', { replace: true });
      return;
    }

    // Com org, se estiver em /onboarding → redirecionar para o app
    if (currentPath === '/onboarding') {
      navigateRef.current('/chat', { replace: true });
    }
  }, [user?.id, organizations.length, authLoading, orgLoading, loadError]);

  // Loading enquanto autenticado e ainda carregando: manter tela roxa se veio de cross-domain
  if (user && (authLoading || orgLoading)) {
    if (isPostCrossDomainLogin) return <CrossDomainLoader />;
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Sem org carregada: não renderizar filhos enquanto o redirect não acontece
  if (user && !authLoading && !orgLoading && organizations.length === 0) {
    return null;
  }

  return children ? <>{children}</> : <Outlet />;
}
